<template>

<app-content :loading="is.loading && !is.initialised">

	<app-content-head :title="title" back="Convention.Badges.Holders">

		<app-content-head-actions-item v-if="!isNew && $permissions.badges.holders.codes.enabled" text="Code" icon="qrcode" v-on:click="onQRClick" />
		
		<app-content-head-actions-item v-if="!isNew && $permissions.badges.holders.labels.enabled" text="Badge" icon="badge" v-on:click="onBadgeClick" />

		<template v-if="$authorised('con/badges/access', 'write')">

			<app-content-head-actions-item :divide="true" v-if="!isNew && $convention.setup.service.registration" theme="grey" text="Comp" icon="addon" v-on:click="onAddClick" />
			
			<app-content-head-actions-item v-if="!isNew && $convention.settings.badges.stacking.enabled" text="Stack" icon="badges.stack" v-on:click="onStackClick" />
			
			<app-content-head-actions-item v-if="$permissions.badges.holders.edit.enabled" :divide="!isNew" :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
			
			<app-content-head-actions-item v-if="$permissions.badges.holders.edit.enabled" :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />
			
			<app-content-head-actions-item v-if="!isNew && $permissions.badges.holders.delete.enabled" :disabled="isNew" :danger="true" icon="delete" text="Delete" v-on:click="onDeleteClick" />

		</template>

	</app-content-head>

	<app-content-body :loading="is.loading && is.initialised" v-if="is.initialised">

		<app-content-summary v-if="!isNew">

			<app-content-summary-item :is-fixed="true" label="Status" :tag="$constants.badge.statusName[item.status]" :status="$constants.badge.statusTheme[item.status]" :theme="$constants.badge.statusTheme[item.status]" />

			<app-content-summary-item label="Ref." :value="item.reference" />

			<app-content-summary-item label="Public No." :value="item.referenceRandom" />

			<app-content-summary-item label="Name" :value="item.name" :grow="true" />

			<app-content-summary-item label="Method" :value="$constants.badge.methodName[item.method]" :is-fixed="true" />
			
			<app-content-summary-item label="Sale" type="convention/registration/sale" :item="item.sale" v-if="item.sale" />
			
			<app-content-summary-item label="Created on" :value="item.date | localDate('MM/DD/YYYY h:mma')" />

			<app-content-summary-item label="Checked in" :value="item.received | localDate('MM/DD/YYYY h:mma')" v-if="item.received" />

			<app-content-summary-item label="Checked in" value="No" v-if="!item.received" />

		</app-content-summary>

		<app-content-box :readonly="$authorised('con/badges/access', 'read')" v-if="isNew">

			<app-input-text v-if="!$convention.setup.service.registration" label="External Ref." v-model="model.referenceExternal" notes="An external reference code for backwards compatibility with existing badge QR codes." :validation="$v.model.referenceExternal" placeholder="Enter reference" :maxlength="128" />
			
			<app-input-text label="First name" v-model="model.firstname" :validation="$v.model.firstname" placeholder="Enter first name" :maxlength="32" />
			
			<app-input-text label="Last name" v-model="model.lastname" :validation="$v.model.lastname" placeholder="Enter last name" :maxlength="32" />
			
			<app-input-account v-if="hasAccounts" label="E-mail" type="badge" :text="emailMessages" v-model="model.email" v-on:exists="onExistsChange" :validation="$v.model.email" />
			
			<app-input-select v-if="$permissions.badges.types.enabled && references.types.length" label="Type" v-model="model.type" :validation="$v.model.type" placeholder="Select badge type" :options="typeOptions" />
			
			<app-input-select v-if="$permissions.badges.groups.enabled && references.groups.length" label="Group" v-model="model.group" :validation="$v.model.group" placeholder="Select optional group" :options="groupOptions" />
			
			<app-input-toggle v-if="$convention.setup.service.registration" label="Requires Purchase" notes="This badge will consume the next purchased or comped badge that this user receives." v-model="model.status" :validation="$v.model.status" :options="statusOptions" />

		</app-content-box>

		<app-content-box :tabs="tabs" :readonly="$authorised('con/badges/access', 'read')" v-if="!isNew">

			<template v-slot:badge>

				<app-input-text v-if="!$convention.setup.service.registration" label="External Ref." v-model="model.referenceExternal" notes="An external reference code for backwards compatibility with existing badge QR codes." :validation="$v.model.referenceExternal" placeholder="Enter reference" :maxlength="128" />
				
				<app-input-text label="First name" v-model="model.firstname" :validation="$v.model.firstname" placeholder="Enter first name" :maxlength="32" />
				
				<app-input-text label="Last name" v-model="model.lastname" :validation="$v.model.lastname" placeholder="Enter last name" :maxlength="32" />
				
				<app-input-text label="E-mail" :has-copy="true" v-model="model.email" :locked="true" notes="You cannot change e-mail addresses as these are tied to platform accounts. Please contact Cardboard Events support for assistance." />
				
				<app-input-select v-if="$permissions.badges.types.enabled && references.types.length && !item.stacked" label="Type" v-model="model.type" :validation="$v.model.type" placeholder="Select badge type" :options="typeOptions" />
				
				<app-input-select v-if="$permissions.badges.groups.enabled && references.groups.length" :allowDeselect="true" label="Group" v-model="model.group" :validation="$v.model.group" placeholder="Select optional group" :options="groupOptions" />

			</template>

			<template v-slot:capture>

				<com-capture :item="item" />

			</template>

			<template v-slot:stacked>

				<com-stacked :item="item" v-on:remove="onRemoveStack($event)" />

			</template>

			<template v-slot:items>

				<com-items :items="references.addons" />

			</template>

			<template v-slot:linked>

				<com-linked :items="references.linked" />

			</template>

		</app-content-box>

		<app-content-box :tabs="content" :readonly="$authorised('con/badges/access', 'read')" v-if="!isNew">

			<template v-slot:plays>

				<com-plays :items="references.loans" />

			</template>

			<template v-slot:events>

				<com-events :items="references.events" />

			</template>

			<template v-slot:submissions>

				<com-submissions :items="references.submissions" />

			</template>

			<template v-slot:purchases>

				<com-purchases :items="references.purchases" />

			</template>

			<template v-slot:slots>

				<com-slots :items="references.assignments" />

			</template>

		</app-content-box>

	</app-content-body>

	<app-input-items-find title="Find add-on to comp" v-if="is.finding && $authorised('con/badges/access', 'write')" api="convention/registration/item/variant" v-on:save="onAddSaveClick" v-on:close="is.finding = false" />
	
	<app-input-items-find title="Find badge to stack" placeholder="Search by name" v-if="is.stacking && $authorised('con/badges/access', 'write')" api="convention/badge/type" :exclude="excludeStacked" v-on:save="onStackSaveClick" v-on:close="is.stacking = false" />

	<com-confirm title="Remove stacked badge" method="delete" v-if="is.removingStack && $authorised('con/badges/access', 'write')" :api="['convention/badges/holder', item.id, 'types', is.removingStack]" v-on:confirmed="onRemoveStackComplete" v-on:close="is.removingStack = false">

		Are you sure you wish to remove the stacked <b>{{ confirmStackName }}</b> badge?

	</com-confirm>

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required, requiredIf, maxLength, email } from 'vuelidate/lib/validators'

import comConfirm from './common/panel/Confirm'
import comItems from './conventionbadgesholder/Items'
import comCapture from './conventionbadgesholder/Capture'
import comStacked from './conventionbadgesholder/Stacked'
import comPlays from './conventionbadgesholder/Plays'
import comEvents from './conventionbadgesholder/Events'
import comPurchases from './conventionbadgesholder/Purchases'
import comSlots from './conventionbadgesholder/Slots'
import comSubmissions from './conventionbadgesholder/Submissions'
import comLinked from './conventionbadgesholder/Linked'

export default {

	mixins: [mixForm],

	components: {
		'com-confirm': comConfirm,
		'com-capture': comCapture,
		'com-items': comItems,
		'com-plays': comPlays,
		'com-events': comEvents,
		'com-purchases': comPurchases,
		'com-slots': comSlots,
		'com-stacked': comStacked,
		'com-linked': comLinked,
		'com-submissions': comSubmissions
	},

	data: function() {

		return {
			readonly: this.$authorised('con/badges/access', 'read'),
			is: {
				finding: false,
				stacking: false,
				removingStack: false
			},
			forceTab: false,
			statusTagType: {
				0: 3,
				1: 1
			},
			statusOptions: {
				0: 'Yes',
				1: 'No'
			},
			emailMessages: {
				info: 'The e-mail address used must not already be associated with an existing badge for this convention.',
				added: 'This e-mail address is already associated with a badge.',
				exists: 'This e-mail address is already associated with an existing Cardboard Events account but they do not have a badge.',
				register: 'A Cardboard Events login will be generated for this e-mail address and will be associated with their badge. The login credentials will automatically be e-mailed to the user.'
			},
			references: {
				types: [],
				groups: [],
				methods: [],
				status: [],
				events: [],
				assignments: [],
				addons: [],
				loans: [],
				purchases: [],
				submissions: [],
				linked: []
			},
			model: {
				id: false,
				referenceExternal: '',
				reference: '',
				firstname: '',
				lastname: '',
				email: '',
				group: 0,
				method: 2,
				status: 1,
				type: 0
			},
			added: false
		}

	},

	validations: {
		model: {
			firstname: {
				required,
				maxLength: maxLength(32)
			},
			lastname: {
				required,
				maxLength: maxLength(32)
			},
			referenceExternal: {
				requiredIf: requiredIf(function() {

					return !this.$convention.setup.service.registration

				})
			},
			type: {
				requiredIf: requiredIf(function() {

					return this.$permissions.badges.types.enabled && this.references.types.length

				})
			},
			method: {
				requiredIf: requiredIf(function() {

					return this.$convention.setup.service.registration

				})
			},
			status: {
				requiredIf: requiredIf(function() {

					return this.$convention.setup.service.registration

				})
			},
			email: {
				requiredIf: requiredIf(function() {

					return this.hasAccounts

				}),
				email
			}
		}
	},

	computed: {

		tabs: function() {

			var tabs = [
				{ name: 'badge', text: 'Badge Setup' }
			];

			tabs.push({ name: 'capture', text: 'Data Capture', notPadded: true, count: this.$_.keys(this.item.capture).length })
			
			if (this.$convention.settings.badges.stacking.enabled) tabs.push({ name: 'stacked', text: 'Stacked Badges', notPadded: true, count: (this.item.stacked) ? this.item.stacked.length : 0 })
			
			tabs.push({ name: 'items', text: 'Add-ons & Tickets', notPadded: true, count: this.item.addons.length })
			
			tabs.push({ name: 'linked', text: 'Linked Badges', notPadded: true, count: this.references.linked.length })
			
			return tabs

		},

		content: function() {

			var tabs = [
				{ name: 'purchases', text: 'Purchases', count: this.item.count.registration.purchases, notPadded: true },
				{ name: 'events', text: 'Scheduled Events', count: this.item.count.schedule.events, notPadded: true },
				{ name: 'submissions', text: 'Event Submissions', count: this.item.count.schedule.suggestions, notPadded: true },
				{ name: 'plays', text: 'Checkout Plays', count: this.item.count.checkout.plays, notPadded: true },
				{ name: 'slots', text: 'Volunteer Assignments', count: this.item.count.volunteer.slots, notPadded: true }
			];

			return tabs

		},

		confirmStackName: function() {

			if (this.is.removingStack) {

				return this.$_.findWhere(this.item.stacked, {
					id: this.is.removingStack
				}).name

			} else {

				return ''

			}

		},

		excludeStacked: function() {

			return this.$_.pluck(this.item.stacked, 'type')

		},

		hasAccounts: function() {

			return this.$convention.setup.service.registration || this.$convention.setup.service.schedule

		},

		typeOptions: function() {

			var options = {}

			this.$_.each(this.references.types, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		groupOptions: function() {

			var options = {}

			this.$_.each(this.references.groups, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		methodOptions: function() {

			return this.references.methods

		}

	},

	methods: {

		onRemoveStackComplete: async function() {

			await this.loadItem()

			this.is.removingStack = false

		},

		onRemoveStack: function(stack) {

			if (stack.sale) {

				this.$router.push({
					name: 'Convention.Registration.Sales.Edit',
					params: {
						id: stack.sale.id
					}
				})

			} else {

				this.is.removingStack = stack.id

			}

		},

		onExistsChange: function(e) {

			this.added = e.added

		},

		onAddSaveClick: function(addons) {

			this.is.loading = true

			this.$api.post(['convention/badges/holder', this.model.id, 'variants'], {
				keys: addons
			}).then(function() {

				this.load()

			}.bind(this))

			this.is.finding = false

		},

		onStackSaveClick: function(types) {

			this.is.loading = true

			this.$api.post(['convention/badges/holder', this.model.id, 'types'], {
				keys: types
			}).then(function() {

				this.load()

			}.bind(this))

			this.is.stacking = false

		},

		onAddClick: function() {

			this.is.finding = true

		},

		onStackClick: function() {

			this.is.stacking = true

		},

		onQRClick: function() {

			this.is.loading = true

			this.$api.get(['qr', this.model.id]).then(function(json) {

				this.$present(json.qr)

				this.is.loading = false

			}.bind(this))

		},

		onBadgeClick: function() {

			this.is.loading = true

			this.$api.get(['badge', this.model.id]).then(function(json) {

				this.$present(json.badge)

				this.is.loading = false

			}.bind(this))

		}

	}

}

</script>

<style scoped>

</style>